import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { getImageSrc, getItemsArrayFromSelectedBoxUsingItems, hexToRgba, hexToRgbaPlain, uploadFile } from '../utils/utils';
import { AppDispatch, RootState } from '../app/store';
import { useDispatch, useSelector } from 'react-redux';
import { createAllBoxVideos, resetCreateAllBoxVideosStatus } from '../features/videoSlice';
import { ToastContainer, toast } from 'react-toastify';
import BackButton from '../components/back_button';
import { Spinner } from '../styles/GlobalStyles';
import { Button } from '../styles/manage_box_page_styles';
import { get1000Items, getBoxDetailsById } from '../features/boxSlice';
import { useParams } from 'react-router-dom';
import ReactSelect, { SingleValue, components, StylesConfig } from 'react-select';
import { Item } from '../modal/Item';

// Styled Components
const Row = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Column = styled.div`
  flex: 1; /* Adjusts to occupy equal space */
  padding: 0 8px; /* Tighter padding */
`;

const FormWrapper = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px; /* Reduced gap for a tighter form */
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px; /* Smaller gap for more compact section */
  border-radius: 8px; /* Rounded corners */
  background-color: #f0ffff; /* Light gray background color */
  padding: 20px; /* Padding to prevent inner elements from touching the border */
`;



const Label = styled.label`
  font-weight: 600; /* Slightly reduced boldness */
  font-size: 14px; /* Adjusted font size for elegance */
`;

const Input = styled.input`
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 100%;
  font-size: 14px; /* Smaller font for cleaner look */
`;

const Select = styled.select`
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 100%;
  font-size: 14px; /* Consistent with Input */
`;

const PreviewContainer = styled.div`
  display: flex;
  gap: 10px; /* Reduced gap for tighter alignment */
  align-items: center;
`;

const ImagePreview = styled.img`
  width: 50px; /* Slightly smaller preview */
  height: 50px;
  object-fit: cover;
  border: 1px solid #ddd;
  border-radius: 4px;
`;

const ErrorMessage = styled.span`
  color: red;
  font-size: 12px;
  margin-top: -8px; /* Tighter error message placement */
`;

// Styled component for the image
const Image = styled.img`
  width: 40px;
  height: 40px;
  margin-right: 10px;
  vertical-align: middle;
    object-fit: contain; /* Ensures the entire image is visible within the container while maintaining its aspect ratio */
  background-color: #f0f0f0; /* Light background color */
  display: block; /* Ensures the image takes up the container space properly */
`;

const Title = styled.h1`
  text-align: center;
  color: #333;
`;


// Define a union type for form data
type BoxDataType = {
    type: 'text' | 'url';
    content: string;
} & (
        | {
            type: 'text'; // For 'text', bg_color and text_color are required
            bg_color: string;
            text_color: string;
        }
        | {
            type: 'url'; // For 'url', bg_color and text_color will be optional
            bg_color?: string;
            text_color?: string;
        }
    );

type FormDataTypeBox = {
    kind: string;
    box_id: String;
    video_width: string;
    video_height: string;
    video_bg: string;
    box_left_side: BoxDataType;
    box_right_side: BoxDataType;
    box_top_side: BoxDataType;
    item_1_type: 'url' | 'file';
    item_1: string;
    item_2_type: 'url' | 'file';
    item_2: string;
    item_3_type: 'url' | 'file';
    item_3: string;
};

// Custom Option Component for react-select
const OptionComponent = (props: any) => {
    return (
        <components.Option {...props}>
            <Image src={props.data.value} alt={props.data.label} />
            {props.data.label}
        </components.Option>
    );
};


// Custom SingleValue Component for react-select
const SingleValueComponent = (props: any) => {
    return (
        <components.SingleValue {...props}>
            <Image src={props.data.value} alt={props.data.label} />
            {props.data.label}
        </components.SingleValue>
    );
};

// Styles for react-select
const customStyles: StylesConfig<any, false> = {
    control: (provided) => ({
        ...provided,
        borderColor: '#ccc',
    }),
    option: (provided) => ({
        ...provided,
        display: 'flex',
        alignItems: 'center',
    }),
    singleValue: (provided) => ({
        ...provided,
        display: 'flex',
        alignItems: 'center',
    }),
};

const AllVideosGeneratorForBox: React.FC = () => {

    const dispatch: AppDispatch = useDispatch();
    const { videos, isCreateVideoInprogress, createAllBoxVideosError, createAllBoxVideosStatus, createAllBoxVideosSuccessMessage } = useSelector((state: RootState) => state.videos);

    const { selectedBox } = useSelector(
        (state: RootState) => state.boxes
    );


    const [formData, setFormData] = useState<FormDataTypeBox>({
        "kind": "0",
        "box_id": "",
        video_width: '1000',
        video_height: '1000',
        video_bg: '#ffffff',
        box_left_side: {
            type: 'url',
            content: '',
            bg_color: '#ffffff',
            text_color: '#000000'
        },
        box_right_side: {
            type: 'url',
            content: '',
            bg_color: '#ffffff',
            text_color: '#000000'
        },
        box_top_side: {
            type: 'url',
            content: '',
            bg_color: '#ffffff',
            text_color: '#000000'
        },
        item_1_type: 'url',
        item_1: '',
        item_2_type: 'url',
        item_2: '',
        item_3_type: 'url',
        item_3: ''
    });

    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [errorMessage, setErrorMessage] = useState("");
    const [showToast, setShowToast] = useState(false);
    const { boxId } = useParams<{ boxId: string }>();
    const [itemsArray, setItemsArray] = useState<Item[]>([]);


    useEffect(() => {
        if (showToast && errorMessage) {
            // Show the toast
            toast.error(errorMessage);
            // Reset showToast to prevent repeated toasts
            setShowToast(false);
        }
    }, [showToast, errorMessage]);

    const triggerError = (message: string) => {
        setErrorMessage(message);
        setShowToast(true);
    };


    useEffect(() => {
        if (createAllBoxVideosStatus === "succeeded") {
            toast(createAllBoxVideosSuccessMessage, {
                autoClose: 3000,
                onClose: () => { },
            });
            dispatch(resetCreateAllBoxVideosStatus());
        } else if (createAllBoxVideosStatus === "failed") {
            toast(`Error creating item: ${createAllBoxVideosError}`, {
                autoClose: 3000,
                onClose: () => { },
            });
            dispatch(resetCreateAllBoxVideosStatus());
        }
    }, [createAllBoxVideosStatus, createAllBoxVideosError, createAllBoxVideosSuccessMessage, dispatch]);


    const fetchItems = () => {
        get1000Items()
            .then((itemsArray) => {
                if (selectedBox) {
                    setItemsArray(getItemsArrayFromSelectedBoxUsingItems(itemsArray, selectedBox));
                } 
            })
            .catch((error: any) => {
                console.error("Error fetching items:", error);
            });
    };

    useEffect(() => {
        if (selectedBox) {
            fetchItems();
        } else {
            const idToUse = boxId;
            // Check if idToUse is a string before dispatching
            if (idToUse) {
                dispatch(getBoxDetailsById(idToUse)).unwrap();
            } else {
                fetchItems();
            }
        }
    }, [dispatch, selectedBox]);



    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        const keys = name.split('.');

        setFormData((prev) => {
            const updatedSection = prev[keys[0] as keyof FormDataTypeBox];

            // Ensure the section being updated is indeed an object
            if (typeof updatedSection === 'object' && updatedSection !== null) {
                return {
                    ...prev,
                    [keys[0] as keyof FormDataTypeBox]: {
                        ...updatedSection,
                        [keys[1] as keyof typeof updatedSection]: value
                    }
                };
            }
            return {
                ...prev,
                [name as keyof FormDataTypeBox]: value
            };
        });

        // Clear the error for the field that was changed
        setErrors((prevErrors) => ({ ...prevErrors, [name]: '' }));
    };


    // Create refs for file inputs
    const fileInputRefs = {
        box_left_side: useRef<HTMLInputElement | null>(null),
        box_right_side: useRef<HTMLInputElement | null>(null),
        box_top_side: useRef<HTMLInputElement | null>(null),
        item_1: useRef<HTMLInputElement | null>(null),
        item_2: useRef<HTMLInputElement | null>(null),
        item_3: useRef<HTMLInputElement | null>(null),

    };

    const handleFileUpload = (side: keyof Pick<FormDataTypeBox, 'box_left_side' | 'box_right_side' | 'box_top_side' | 'item_1' | 'item_2' | 'item_3'>) => async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            try {
                const [uploadFileName, errorMessage] = await uploadFile(file); // Assuming uploadFile function is available
                if (!errorMessage) {

                    if (side === "item_1" || side === "item_2" || side === "item_3") {
                        setFormData((prevData) => ({
                            ...prevData,
                            [side]: getImageSrc(uploadFileName), // Update content with the uploaded file URL
                        }));
                    } else {
                        setFormData((prevData) => ({
                            ...prevData,
                            [side]: {
                                ...prevData[side], // Here, side is guaranteed to be BoxDataType
                                content: getImageSrc(uploadFileName), // Update content with the uploaded file URL
                            },
                        }));
                    }


                    // Clear the file input safely
                    const fileInput = fileInputRefs[side].current;
                    if (fileInput) {
                        fileInput.value = ''; // Safely clear the file input
                    }
                } else {
                    console.error("File upload error:", errorMessage);
                }
            } catch (error) {
                console.error("Unexpected error:", error);
            }
        }

        // Clear the error for the field that was changed
        setErrors((prevErrors) => ({ ...prevErrors, [side]: '' }));

        // validateForm();

    };


    const validateForm = () => {
        let formIsValid = true;
        const newErrors: { [key: string]: string } = {};

        // Add validation for required fields
        if (!formData.video_width) {
            formIsValid = false;
            newErrors.video_width = 'Video width is required';
        }

        if (!formData.video_height) {
            formIsValid = false;
            newErrors.video_height = 'Video height is required';
        }

        if (!formData.box_left_side.content) {
            formIsValid = false;
            newErrors['box_left_side.content'] = 'Box Left Side content is required';
        }

        if (!formData.box_right_side.content) {
            formIsValid = false;
            newErrors['box_right_side.content'] = 'Box Right Side content is required';
        }

        if (!formData.box_top_side.content) {
            formIsValid = false;
            newErrors['box_top_side.content'] = 'Box Top Side content is required';
        }

        if (!formData.item_1) {
            formIsValid = false;
            newErrors['item_1'] = 'Item1 is required';
        }

        if (!formData.item_2) {
            formIsValid = false;
            newErrors['item_2'] = 'Item2 is required';
        }

        if (!formData.item_3) {
            formIsValid = false;
            newErrors['item_3'] = 'Item3 is required';
        }

        // Add any other necessary validations here...

        setErrors(newErrors);

        const allErrorsString = Object.values(newErrors).join(', ');
        triggerError(allErrorsString)
        return formIsValid;
    };




    const renderPreview = (content: string, type: 'url' | 'file') => {
        if (type === 'file' && content) {
            return <ImagePreview src={content} alt="Preview" />;
        }
        if (type === 'url' && content) {
            return <ImagePreview src={content} alt="Preview" />;
        }
        return null;
    };



    // Function to clean the form data
    function cleanFormData(
        data: FormDataTypeBox
    ): Omit<FormDataTypeBox, 'item_1_type' | 'item_2_type' | 'item_3_type'> & { box_id: string } {
        const cleanBoxSide = (box: BoxDataType): BoxDataType => {
            if (box.type === 'url') {
                const { bg_color, text_color, ...rest } = box;
                return rest;
            }

            box.bg_color = hexToRgbaPlain(box.bg_color);
            box.text_color = hexToRgbaPlain(box.text_color);
            return box;
        };

        const { item_1_type, item_2_type, item_3_type, ...cleanedData } = data;

        if (cleanedData.video_bg) {
            cleanedData.video_bg = hexToRgbaPlain(cleanedData.video_bg, 0);
        }

        const boxId = selectedBox?.id;

        if (!boxId) {
            throw new Error("box_id is required but missing.");
        }

        return {
            ...cleanedData,
            box_left_side: cleanBoxSide(data.box_left_side),
            box_right_side: cleanBoxSide(data.box_right_side),
            box_top_side: cleanBoxSide(data.box_top_side),
            box_id: boxId,
        };
    }


    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault(); // Prevent the default form submission

        // Validate the form before submitting
        if (validateForm()) {
            const cleanedData = cleanFormData(formData);
            console.log(JSON.stringify(cleanedData, null, 2));

            dispatch(createAllBoxVideos(cleanedData));

        } else {
            console.log('Form validation failed');
        }
    };


    const handleItemSelect = (itemKey: keyof typeof formData, selected: SingleValue<any>) => {
        setFormData((prev) => ({
            ...prev,
            [itemKey]: selected ? selected.value : '',
        }));
    };

    const mapToOption = (item: Item) => ({
        value: getImageSrc(item.picture),
        label: item.name,
        item,
    });

    return (
        <>
            <Row>
                <BackButton />
            </Row>
        
            <Title>{selectedBox?.name ? `${selectedBox.name} videos creation page` : "Box videos creation page"}</Title>
            <Row>
            <h3>Video Settings</h3>
            </Row>
            <Row>
                <ToastContainer />
                <Column>
                    <FormWrapper onSubmit={handleSubmit}>  {/* Add the onSubmit handler here */}
                        {/* <h3>Video Settings</h3> */}
                        <Section>
                            <Label>Video Width:</Label>
                            <Input
                                type="number"
                                name="video_width"
                                value={formData.video_width}
                                onChange={handleChange}
                                placeholder="Enter Video Width"
                            />
                            {errors.video_width && <ErrorMessage>{errors.video_width}</ErrorMessage>}

                            <Label>Video Height:</Label>
                            <Input
                                type="number"
                                name="video_height"
                                value={formData.video_height}
                                onChange={handleChange}
                                placeholder="Enter Video Height"
                            />
                            {errors.video_height && <ErrorMessage>{errors.video_height}</ErrorMessage>}

                            <Label>Video Background Color (RGBA):</Label>
                            <Input
                                type="color"
                                name="video_bg"
                                value={formData.video_bg}
                                onChange={handleChange}
                                placeholder="Select Background Color"
                            />
                            <Label>{hexToRgbaPlain(formData.video_bg, 0)}</Label>

                        </Section>

                        {/* Box Left Side */}
                        <h3>Box Left Side</h3>
                        <Section>
                            <Label>Type:</Label>
                            <Select name="box_left_side.type" value={formData.box_left_side.type} onChange={handleChange}>
                                <option value="text">Text</option>
                                <option value="url">Url</option>
                            </Select>
                            <Label>Content:</Label>
                            {formData.box_left_side.type === 'text' ? (
                                <>
                                    <Input
                                        type="text"
                                        name="box_left_side.content"
                                        value={formData.box_left_side.content}
                                        onChange={handleChange}
                                        placeholder="Enter text content"
                                    />
                                    {errors['box_left_side.content'] && <ErrorMessage>{errors['box_left_side.content']}</ErrorMessage>}

                                    <Label>Background Color (RGBA):</Label>
                                    <Input
                                        type="color"
                                        name="box_left_side.bg_color"
                                        value={formData.box_left_side.bg_color || ''}
                                        onChange={handleChange}
                                    />
                                    <Label>{hexToRgbaPlain(formData.box_left_side.bg_color)}</Label>

                                    <Label>Text Color (RGB):</Label>
                                    <Input
                                        type="color"
                                        name="box_left_side.text_color"
                                        value={formData.box_left_side.text_color || ''}
                                        onChange={handleChange}
                                    />
                                    <Label>{hexToRgbaPlain(formData.box_left_side.text_color)}</Label>
                                </>
                            ) : (
                                <>
                                    <Input
                                        type="file"
                                        accept="image/*"
                                        name="box_left_side.content"
                                        ref={fileInputRefs.box_left_side} // Reference for clearing
                                        onChange={handleFileUpload('box_left_side')}
                                    />
                                    <Input
                                        type="text"
                                        name="box_left_side.content"
                                        value={formData.box_left_side.content}
                                        onChange={handleChange}
                                        placeholder="Enter image URL"
                                    />
                                    {errors['box_left_side.content'] && <ErrorMessage>{errors['box_left_side.content']}</ErrorMessage>}

                                    <PreviewContainer>
                                        {renderPreview(formData.box_left_side.content, formData.box_left_side.type)}
                                    </PreviewContainer>
                                </>
                            )}


                        </Section>

                        {/* Box Right Side */}
                        <h3>Box Right Side</h3>
                        <Section>
                            <Label>Type:</Label>
                            <Select name="box_right_side.type" value={formData.box_right_side.type} onChange={handleChange}>
                                <option value="text">Text</option>
                                <option value="url">Url</option>
                            </Select>
                            <Label>Content:</Label>
                            {formData.box_right_side.type === 'text' ? (
                                <>
                                    <Input
                                        type="text"
                                        name="box_right_side.content"
                                        value={formData.box_right_side.content}
                                        onChange={handleChange}
                                        placeholder="Enter text content"
                                    />
                                    {errors['box_right_side.content'] && <ErrorMessage>{errors['box_right_side.content']}</ErrorMessage>}

                                    <Label>Background Color (RGBA):</Label>
                                    <Input
                                        type="color"
                                        name="box_right_side.bg_color"
                                        value={formData.box_right_side.bg_color || ''}
                                        onChange={handleChange}
                                    />
                                    <Label>{hexToRgbaPlain(formData.box_right_side.bg_color)}</Label>

                                    <Label>Text Color (RGB):</Label>
                                    <Input
                                        type="color"
                                        name="box_right_side.text_color"
                                        value={formData.box_right_side.text_color || ''}
                                        onChange={handleChange}
                                    />
                                    <Label>{hexToRgbaPlain(formData.box_right_side.text_color)}</Label>

                                </>
                            ) : (
                                <>
                                    <Input
                                        type="file"
                                        accept="image/*"
                                        name="box_right_side.content"
                                        ref={fileInputRefs.box_right_side} // Reference for clearing
                                        onChange={handleFileUpload('box_right_side')}
                                    />
                                    <Input
                                        type="text"
                                        name="box_right_side.content"
                                        value={formData.box_right_side.content}
                                        onChange={handleChange}
                                        placeholder="Enter image URL"
                                    />
                                    {errors['box_right_side.content'] && <ErrorMessage>{errors['box_right_side.content']}</ErrorMessage>}

                                    <PreviewContainer>
                                        {renderPreview(formData.box_right_side.content, formData.box_right_side.type)}
                                    </PreviewContainer>
                                </>
                            )}

                        </Section>

                        {/* Box Top Side */}
                        <h3>Box Top Side</h3>
                        <Section>
                            <Label>Type:</Label>
                            <Select name="box_top_side.type" value={formData.box_top_side.type} onChange={handleChange}>
                                <option value="text">Text</option>
                                <option value="url">Url</option>
                            </Select>
                            <Label>Content:</Label>
                            {formData.box_top_side.type === 'text' ? (
                                <>
                                    <Input
                                        type="text"
                                        name="box_top_side.content"
                                        value={formData.box_top_side.content}
                                        onChange={handleChange}
                                        placeholder="Enter text content"
                                    />
                                    {errors['box_top_side.content'] && <ErrorMessage>{errors['box_top_side.content']}</ErrorMessage>}

                                    <Label>Background Color (RGBA):</Label>
                                    <Input
                                        type="color"
                                        name="box_top_side.bg_color"
                                        value={formData.box_top_side.bg_color || ''}
                                        onChange={handleChange}
                                    />
                                    <Label>{hexToRgbaPlain(formData.box_top_side.bg_color)}</Label>

                                    <Label>Text Color (RGB):</Label>
                                    <Input
                                        type="color"
                                        name="box_top_side.text_color"
                                        value={formData.box_top_side.text_color || ''}
                                        onChange={handleChange}
                                    />
                                    <Label>{hexToRgbaPlain(formData.box_top_side.text_color)}</Label>

                                </>
                            ) : (
                                <>
                                    <Input
                                        type="file"
                                        accept="image/*"
                                        name="box_top_side.content"
                                        ref={fileInputRefs.box_top_side} // Reference for clearing
                                        onChange={handleFileUpload('box_top_side')}
                                    />
                                    <Input
                                        type="text"
                                        name="box_top_side.content"
                                        value={formData.box_top_side.content}
                                        onChange={handleChange}
                                        placeholder="Enter image URL"
                                    />
                                    {errors['box_top_side.content'] && <ErrorMessage>{errors['box_top_side.content']}</ErrorMessage>}

                                    <PreviewContainer>
                                        {renderPreview(formData.box_top_side.content, formData.box_top_side.type)}
                                    </PreviewContainer>
                                </>
                            )}


                        </Section>

                        <h3>Items</h3>
                        <Section>
                            <h3>Items</h3>
                            <label>Item 1:</label>
                            <ReactSelect
                                value={itemsArray.map(mapToOption).find((opt) => opt.value === formData.item_1)}
                                onChange={(selected) => handleItemSelect('item_1', selected)}
                                options={itemsArray.map(mapToOption)}
                                components={{ SingleValue: SingleValueComponent, Option: OptionComponent }}
                                styles={customStyles}
                            />
                            {formData.item_1 && <ImagePreview src={formData.item_1} alt="Item 1 Preview" />}

                            <label>Item 2:</label>
                            <ReactSelect
                                value={itemsArray.map(mapToOption).find((opt) => opt.value === formData.item_2)}
                                onChange={(selected) => handleItemSelect('item_2', selected)}
                                options={itemsArray.map(mapToOption)}
                                components={{ SingleValue: SingleValueComponent, Option: OptionComponent }}
                                styles={customStyles}
                            />
                            {formData.item_2 && <ImagePreview src={formData.item_2} alt="Item 2 Preview" />}

                            <label>Item 3:</label>
                            <ReactSelect
                                value={itemsArray.map(mapToOption).find((opt) => opt.value === formData.item_3)}
                                onChange={(selected) => handleItemSelect('item_3', selected)}
                                options={itemsArray.map(mapToOption)}
                                components={{ SingleValue: SingleValueComponent, Option: OptionComponent }}
                                styles={customStyles}
                            />
                            {formData.item_3 && <ImagePreview src={formData.item_3} alt="Item 3 Preview" />}
                        </Section>

                        <Button
                            primary
                            type="submit"
                            disabled={isCreateVideoInprogress}
                        >
                            {isCreateVideoInprogress && <Spinner />}
                            {isCreateVideoInprogress ? '' : 'Create Video'}
                        </Button>

                    </FormWrapper>
                </Column>

            </Row>
        </>
    );
};

export default AllVideosGeneratorForBox;
